import React, { useState, useEffect } from 'react';
import { Phone, Clock, Calendar, ArrowLeft, ArrowRight, Play, Pause, MessageSquare, Download, FileText, X, Bot, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URL } from '../config/api';
import FloatingParticles from './FloatingParticles';
import EnhancedCard from './EnhancedCard';
import EnhancedButton from './EnhancedButton';
import PageHeader from './ui/PageHeader';
import '../styles/neutral-theme.css';
import '../styles/conversations-modern.css';

const DASHBOARD_CONFIG_FALLBACK = {
  sections: {
    conversations: {
      enabled: true,
      dataSource: 'main',
      fields: {
        transcript: true,
        sentiment: true,
        duration: true,
        created_at: true,
        agent_name: true,
      },
      fieldMappings: {
        transcript: { table: 'conversations', column: 'transcript' },
        sentiment: { table: 'conversations', column: 'sentiment' },
        duration: { table: 'conversations', column: 'duration' },
        created_at: { table: 'conversations', column: 'created_at' },
        agent_name: { table: 'conversations', column: 'agent_name' },
      },
      fieldCalculations: {},
    },
  },
};

const ConversationsPanel = () => {
  const { t } = useLanguage();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentCursor, setCurrentCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize] = useState(50);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  
  // ElevenLabs integration states (siempre usar ElevenLabs)
  const [elevenLabsConversations, setElevenLabsConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [preloadingConversations, setPreloadingConversations] = useState(false);
  const [preloadStatus, setPreloadStatus] = useState(null);
  const [conversationDetails, setConversationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [clientAgentId, setClientAgentId] = useState(null);

  // Dashboard config (para habilitar/ocultar campos)
  const [dashboardConfig, setDashboardConfig] = useState(DASHBOARD_CONFIG_FALLBACK);
  const [configLoading, setConfigLoading] = useState(false);
  
  // Agent selection states
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Cargar DashboardConfig para saber si la sección está habilitada y qué campos mostrar
  useEffect(() => {
    const fetchConfig = async () => {
      setConfigLoading(true);
      try {
        const resp = await fetch(`${API_URL}/api/admin/dashboard-config`, { credentials: 'include' });
        if (resp.ok) {
          const json = await resp.json();
          setDashboardConfig(json.config || DASHBOARD_CONFIG_FALLBACK);
        } else {
          setDashboardConfig(DASHBOARD_CONFIG_FALLBACK);
        }
      } catch (e) {
        setDashboardConfig(DASHBOARD_CONFIG_FALLBACK);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const sectionCfg = (dashboardConfig?.sections?.conversations) || DASHBOARD_CONFIG_FALLBACK.sections.conversations;
  const fieldsCfg = sectionCfg?.fields || {};
  const fieldOn = (key) => (fieldsCfg[key] ?? true);
  const isSectionEnabled = sectionCfg?.enabled !== false;

  // Cargar agentes del cliente al montar (siempre usar ElevenLabs)
  useEffect(() => {
    if (!isSectionEnabled) return;
    
    const loadClientAgents = async () => {
      try {
        // Primero obtener la configuración del cliente
        const configResponse = await fetch(`${API_URL}/api/client/config`);
        const config = await configResponse.json();
        
        if (config.agent_id) {
          setClientAgentId(config.agent_id);
        }
        
        // Obtener el client_id del usuario actual
        const meResponse = await fetch(`${API_URL}/api/me`);
        const meData = await meResponse.json();
        
        if (meData.client && meData.client.id) {
          setLoadingAgents(true);
          
          // Cargar agentes específicos del cliente
          const agentsResponse = await fetch(`${API_URL}/api/clients/${meData.client.id}/agents`);
          if (agentsResponse.ok) {
            const agents = await agentsResponse.json();
            setAvailableAgents(agents);
            
            // Si hay agentes disponibles, seleccionar el primero por defecto
            if (agents.length > 0) {
              setSelectedAgentId(agents[0].agent_id);
              // Cargar conversaciones del primer agente
              await fetchElevenLabsConversations(agents[0].agent_id);
            } else if (config.agent_id) {
              // Si no hay agentes específicos, usar el agent_id del cliente como fallback
              setSelectedAgentId(config.agent_id);
              await fetchElevenLabsConversations(config.agent_id);
            }
          }
        }
      } catch (err) {
        console.error('Error loading client agents:', err);
      } finally {
        setLoadingAgents(false);
      }
    };
    
    loadClientAgents();
  }, [isSectionEnabled]);

  // Empty conversations array - only show data when ElevenLabs agents are configured
  const conversations = [];

  // Empty conversation detail - only populated when ElevenLabs data is available
  const conversationDetail = null;

  // Empty chat messages - only populated when ElevenLabs data is available
  const chatMessages = [];

  const handleConversationSelect = async (conversation) => {
    setSelectedConversation(conversation);
    const source = conversation?.originalData?.source || 'global';
    
    if (conversation.originalData) {
      await fetchConversationDetails(conversation.originalData.conversation_id, source);
    }
  };

  const handleAudioPlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Historial de cursors para navegación hacia atrás
  const [cursorHistory, setCursorHistory] = useState([]);

  const handlePageChange = async (direction) => {
    if (direction === 'next' && hasMore && nextCursor) {
      // Guardar cursor actual en el historial
      setCursorHistory(prev => [...prev, currentCursor]);
      setCurrentCursor(nextCursor);
      setCurrentPageNumber(prev => prev + 1);
      // Recargar conversaciones con el nuevo cursor
      await fetchElevenLabsConversations(selectedAgentId || clientAgentId, nextCursor);
    } else if (direction === 'prev' && cursorHistory.length > 0) {
      // Obtener el cursor anterior del historial
      const previousCursors = [...cursorHistory];
      const previousCursor = previousCursors.pop();
      setCursorHistory(previousCursors);
      setCurrentCursor(previousCursor);
      setCurrentPageNumber(prev => Math.max(1, prev - 1));
      // Recargar conversaciones con el cursor anterior
      await fetchElevenLabsConversations(selectedAgentId || clientAgentId, previousCursor);
    }
  };

  const handleDownloadCall = () => {
    // Aquí iría la lógica para descargar la llamada
  };

  const handleDownloadPDF = () => {
    // Aquí iría la lógica para descargar el PDF
  };

  // Función para iniciar precarga de conversaciones en segundo plano
  const startPreloadConversations = async (agentId) => {
    if (!agentId || !agentId.trim()) {
      return;
    }

    setPreloadingConversations(true);
    setPreloadStatus({ message: 'Iniciando precarga de conversaciones...', progress: 0 });

    try {
      const response = await fetch(`${API_URL}/api/elevenlabs/conversations/preload?agent_id=${encodeURIComponent(agentId)}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setPreloadStatus({
        message: data.message || 'Precarga iniciada en segundo plano',
        status: data.status
      });

      // La precarga continúa en segundo plano, no esperamos
      setTimeout(() => {
        setPreloadingConversations(false);
        setPreloadStatus(null);
      }, 2000);
    } catch (err) {
      console.error('Error iniciando precarga:', err);
      setPreloadStatus({
        message: `Error iniciando precarga: ${err.message}`,
        error: true
      });
      setTimeout(() => {
        setPreloadingConversations(false);
        setPreloadStatus(null);
      }, 3000);
    }
  };

  // Función para manejar el cambio de agente seleccionado
  const handleAgentChange = async (newAgentId) => {
    setSelectedAgentId(newAgentId);
    setLoadingConversations(true);
    setError(null);
    setPreloadStatus(null);
    
    // Resetear paginación al cambiar de agente
    setCurrentCursor(null);
    setNextCursor(null);
    setHasMore(false);
    setCursorHistory([]);
    setCurrentPageNumber(1);
    
    // Iniciar precarga en segundo plano (no bloquea la carga actual)
    if (newAgentId && newAgentId.trim()) {
      startPreloadConversations(newAgentId);
    }
    
    try {
      // Cargar conversaciones (usará cache si está disponible)
      await fetchElevenLabsConversations(newAgentId, null);
    } catch (err) {
      setError(`Error al cargar conversaciones del agente: ${err.message}`);
    }
  };

  // ElevenLabs API functions
  const fetchElevenLabsConversations = async (agentIdToUse = null, cursorToUse = null) => {
    const agentId = agentIdToUse || selectedAgentId || clientAgentId;
    
    if (!agentId || !agentId.trim()) {
      setError('No se encontró Agent ID para este cliente');
      return;
    }

    setLoadingConversations(true);
    setError(null);

    try {
      // source=all: traer llamadas de ElevenLabs Global y ElevenLabs EU (ambas APIs)
      const params = new URLSearchParams({
        agent_id: agentId,
        page_size: pageSize.toString(),
        use_cache: 'true',
        source: 'all'
      });
      if (cursorToUse) {
        params.append('cursor', cursorToUse);
      }

      const response = await fetch(`${API_URL}/api/elevenlabs/conversations?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.conversations && Array.isArray(data.conversations)) {
        // Las conversaciones ya vienen filtradas por agent_id desde el backend
        setElevenLabsConversations(data.conversations);
        
        // Actualizar estado de paginación
        setNextCursor(data.next_cursor || null);
        setHasMore(data.has_more || false);
        setCurrentCursor(cursorToUse);
        
        // Si es la primera página, resetear historial
        if (!cursorToUse) {
          setCursorHistory([]);
          setCurrentPageNumber(1);
        }
      } else {
        setElevenLabsConversations([]);
        setNextCursor(null);
        setHasMore(false);
      }
    } catch (err) {
      setError(`Error al obtener conversaciones: ${err.message}`);
      setElevenLabsConversations([]);
      setNextCursor(null);
      setHasMore(false);
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchConversationDetails = async (conversationId, source = 'global') => {
    setLoadingDetails(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/elevenlabs/conversation/${encodeURIComponent(conversationId)}?source=${encodeURIComponent(source)}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setConversationDetails(data);
      setShowTranscriptionModal(true);
    } catch (err) {
      setError(`Error al obtener detalles: ${err.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  const downloadAudio = async (conversationId, source = 'global') => {
    try {
      const response = await fetch(`${API_URL}/api/elevenlabs/audio/${encodeURIComponent(conversationId)}?source=${encodeURIComponent(source)}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `conversation_${conversationId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Error al descargar audio: ${err.message}`);
    }
  };

  const downloadPDF = async (conversationId, source = 'global') => {
    try {
      const response = await fetch(`${API_URL}/api/elevenlabs/pdf/${encodeURIComponent(conversationId)}?source=${encodeURIComponent(source)}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transcripcion_${conversationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Error al descargar PDF: ${err.message}`);
    }
  };

  const filteredConversations = elevenLabsConversations.filter((conv) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'confirmed') return conv.status === 'done';
    return true;
  });

  const formatElevenLabsConversation = (conv) => {
    // Usar el timestamp real de la llamada, nunca la fecha actual
    let startTime = 'N/A';
    if (conv.start_time_unix_secs) {
      const date = new Date(conv.start_time_unix_secs * 1000);
      if (!isNaN(date.getTime())) {
        startTime = date.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit' 
        });
      }
    } else if (conv.start_time) {
      // Si viene como string ISO
      const date = new Date(conv.start_time);
      if (!isNaN(date.getTime())) {
        startTime = date.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit' 
        });
      }
    }
    
    const duration = conv.call_duration_secs ? 
      `${Math.floor(conv.call_duration_secs / 60)}:${(conv.call_duration_secs % 60).toString().padStart(2, '0')}` : 
      'N/A';

    // Función auxiliar para extraer valor de un campo de data_collection
    const extractValue = (fieldObj) => {
      if (!fieldObj) return null;
      if (typeof fieldObj === 'object' && 'value' in fieldObj) {
        const val = fieldObj.value;
        if (val === null || val === undefined) return null;
        if (typeof val === 'object') return JSON.stringify(val);
        if (Array.isArray(val)) return val.join(', ');
        return String(val);
      }
      if (typeof fieldObj === 'string' || typeof fieldObj === 'number') {
        return String(fieldObj);
      }
      return null;
    };

    // Intentar extraer teléfono llamado (número del cliente) de la conversación
    // Buscar en múltiples ubicaciones posibles
    let phone = 'N/A';
    const phoneVal = extractValue(conv.analysis?.data_collection_results?.telefono) || 
                     extractValue(conv.analysis?.data_collection_results?.phone) || 
                     extractValue(conv.analysis?.data_collection_results?.numero) ||
                     extractValue(conv.analysis?.data_collection_results?.telefono_llamado) ||
                     (conv.phone_number ? String(conv.phone_number) : null) ||
                     (conv.phone ? String(conv.phone) : null) ||
                     (conv.customer_phone ? String(conv.customer_phone) : null);
    
    if (phoneVal) {
      phone = phoneVal;
    } else if (conv.agent_name) {
      // Fallback al nombre del agente si no hay teléfono disponible
      phone = conv.agent_name;
    } else {
      phone = 'Agente Virtual';
    }

    return {
      id: conv.conversation_id,
      phone: phone,
      time: startTime,
      status: conv.status || 'unknown',
      duration: duration,
      source: conv.source || 'global',
      originalData: conv
    };
  };

  // Función para formatear datos de ElevenLabs al formato de visualización
  const getConversationDisplayData = () => {
    if (!conversationDetails) {
      return null;
    }

    // Extraer datos de la estructura real de la API
    const data = conversationDetails;
    const analysis = data.analysis || {};
    const dataCollection = analysis.data_collection_results || {};
    
    // Formatear fecha de inicio - SIEMPRE usar el timestamp de la API, nunca la fecha actual
    let startDate = null;
    if (data.start_time_unix_secs) {
      startDate = new Date(data.start_time_unix_secs * 1000);
    } else if (data.start_time) {
      // Si viene como string ISO
      startDate = new Date(data.start_time);
    }
    
    // Si no hay fecha válida, no mostrar fecha incorrecta
    if (!startDate || isNaN(startDate.getTime())) {
      startDate = null;
    }
    
    // Formatear duración
    const duration = data.call_duration_secs ? 
      `${Math.floor(data.call_duration_secs / 60)}m ${data.call_duration_secs % 60}s` : 
      'N/A';

    // Función auxiliar para extraer valor de un campo de data_collection
    const extractValue = (fieldObj) => {
      if (!fieldObj) return null;
      if (typeof fieldObj === 'object' && 'value' in fieldObj) {
        const val = fieldObj.value;
        if (val === null || val === undefined) return null;
        if (typeof val === 'object') return JSON.stringify(val);
        if (Array.isArray(val)) return val.join(', ');
        return String(val);
      }
      if (typeof fieldObj === 'string' || typeof fieldObj === 'number') {
        return String(fieldObj);
      }
      return null;
    };

    // Extraer teléfono llamado (número del cliente) - puede estar en varios lugares
    let phone = 'N/A';
    const phoneVal = extractValue(dataCollection.telefono) || 
                     extractValue(dataCollection.phone) || 
                     extractValue(dataCollection.numero) ||
                     extractValue(dataCollection.telefono_llamado) ||
                     (data.phone_number ? String(data.phone_number) : null) ||
                     (data.phone ? String(data.phone) : null) ||
                     (data.customer_phone ? String(data.customer_phone) : null);
    if (phoneVal) phone = phoneVal;

    // Extraer campos útiles de data_collection_results que no se están mostrando
    const titular = extractValue(dataCollection.titular) || extractValue(dataCollection.title);
    const deParteDe = extractValue(dataCollection['de-parte-de']) || extractValue(dataCollection.de_parte_de) || extractValue(dataCollection.from);
    const molesto = extractValue(dataCollection.molesto);
    const nombre = extractValue(dataCollection.nombre) || extractValue(dataCollection.name);

    // Determinar origen de llamada (direction)
    let callOrigin = 'Outbound';
    if (data.direction === 'inbound' || data.direction === 'Inbound') {
      callOrigin = 'Inbound';
    } else if (data.direction === 'outbound' || data.direction === 'Outbound') {
      callOrigin = 'Outbound';
    }

    // Determinar estado
    let status = 'Desconocido';
    if (data.status === 'done' || data.status === 'completed') {
      status = 'Realizada';
    } else if (data.status === 'failed' || data.status === 'error') {
      status = 'Fallida';
    } else if (data.status) {
      status = data.status.charAt(0).toUpperCase() + data.status.slice(1);
    }

    // Determinar resultado
    let result = 'Confirmada';
    if (analysis.outcome) {
      result = analysis.outcome;
    } else if (analysis.result) {
      result = analysis.result;
    } else if (data.call_successful === 'true' || data.call_successful === true) {
      result = 'Confirmada';
    } else if (data.call_successful === 'false' || data.call_successful === false) {
      result = 'No confirmada';
    }

    // Determinar quién colgó
    let hungUpBy = 'Agente';
    if (analysis.hung_up_by) {
      hungUpBy = analysis.hung_up_by;
    } else if (data.hung_up_by) {
      hungUpBy = data.hung_up_by;
    } else if (analysis.end_reason === 'user' || analysis.end_reason === 'customer') {
      hungUpBy = 'Cliente';
    } else if (analysis.end_reason === 'agent' || analysis.end_reason === 'system') {
      hungUpBy = 'Agente';
    }

    // Extraer resumen
    let summary = 'Conversación con agente virtual';
    if (analysis.transcript_summary) {
      summary = analysis.transcript_summary;
    } else if (data.transcript_summary) {
      summary = data.transcript_summary;
    } else if (analysis.summary) {
      summary = analysis.summary;
    } else if (data.call_summary_title) {
      summary = data.call_summary_title;
    }

    // Extraer campaña
    let campaign = 'Campaña Virtual';
    if (analysis.campaign_name) {
      campaign = analysis.campaign_name;
    } else if (data.campaign_name) {
      campaign = data.campaign_name;
    } else if (analysis.campaign) {
      campaign = analysis.campaign;
    }

    return {
      phone: phone,
      // Campos útiles de data_collection_results
      titular: titular,
      deParteDe: deParteDe,
      molesto: molesto,
      nombre: nombre,
      callOrigin: callOrigin,
      agent: data.agent_name || 'Agente Virtual',
      campaign: campaign,
      callDate: startDate ? startDate.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }) : 'Fecha no disponible',
      callDateTime: startDate ? startDate : null, // Para uso interno
      status: status,
      result: result,
      duration: duration,
      hungUpBy: hungUpBy,
      summary: summary,
      // Datos adicionales útiles
      conversationId: data.conversation_id,
      agentId: data.agent_id,
      messageCount: data.message_count || 0,
      direction: data.direction,
      rawData: data // Mantener referencia a datos completos para debugging
    };
  };

  return (
    <div className="conversations-panel-container dashboard-container">
      <FloatingParticles count={20} />
      
      <PageHeader
        title={t('conversations.title')}
        subtitle="Historial completo de interacciones con clientes"
        icon={<MessageSquare />}
      />

      {error && (
        <div className="message-enhanced error" style={{ marginTop: '12px' }}>
          {error}
        </div>
      )}

      {/* Indicador de precarga */}
      {preloadingConversations && preloadStatus && (
        <div className="message-enhanced" style={{ 
          marginTop: '12px', 
          background: 'rgba(59, 130, 246, 0.15)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          color: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          borderRadius: '8px'
        }}>
          <Loader2 size={16} className="animate-spin" />
          <span style={{ fontSize: '13px' }}>{preloadStatus.message}</span>
        </div>
      )}

      <div className="conversations-main-layout">
        {/* Panel izquierdo - Lista de conversaciones */}
        <div className="conversations-list-panel">
          <div className="conversations-list-header">
            <div className="conversations-list-title">
              {t('conversations.phoneNumber')} ↓
            </div>
            
            {/* Indicador de carga de agentes */}
            {loadingAgents && (
              <div style={{ 
                padding: '8px', 
                textAlign: 'center', 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                Cargando agentes...
              </div>
            )}
            
            {/* Selector de Agentes */}
            {!loadingAgents && availableAgents.length > 0 && (
              <div className="agent-selector-container" style={{ marginBottom: '12px' }}>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  marginBottom: '6px', 
                  display: 'block' 
                }}>
                  Seleccionar Agente:
                </label>
                <select
                  value={selectedAgentId || ''}
                  onChange={(e) => handleAgentChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {availableAgents.map((agent) => (
                    <option key={agent.agent_id} value={agent.agent_id} style={{ background: '#0c2c6c', color: '#ffffff' }}>
                      {agent.name} ({agent.agent_id.substring(0, 20)}...)
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Mensaje cuando no hay agentes */}
            {!loadingAgents && availableAgents.length === 0 && (
              <div style={{ 
                padding: '8px', 
                textAlign: 'center', 
                color: 'rgba(255, 255, 255, 0.6)', 
                fontSize: '12px',
                marginBottom: '8px',
                fontStyle: 'italic'
              }}>
                No hay agentes configurados para este cliente
              </div>
            )}
            
            <div className="conversation-tabs">
              <button 
                className={`conversation-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                {t('conversations.all')} ({elevenLabsConversations.length})
              </button>
              <button 
                className={`conversation-tab ${activeTab === 'confirmed' ? 'active' : ''}`}
                onClick={() => setActiveTab('confirmed')}
              >
                {t('conversations.confirmed')} ({elevenLabsConversations.filter(c => c.status === 'done').length})
              </button>
            </div>
            )}
          </div>

          <div className="conversations-list">
            {!isSectionEnabled ? (
              <div className="no-conversations-message">
                <AlertCircle size={48} className="no-conversations-icon" />
                <h3>Sección desactivada</h3>
                <p>El administrador desactivó Conversaciones para este cliente.</p>
              </div>
            ) : loadingConversations ? (
              <div className="no-conversations-message">
                <Loader2 size={48} className="no-conversations-icon animate-spin" />
                <h3>Cargando conversaciones…</h3>
                <p>Consultando API de ElevenLabs…</p>
              </div>
            ) : (
              filteredConversations.length > 0 ? (
                filteredConversations.map((conv) => {
                  const formattedConv = formatElevenLabsConversation(conv);
                  return (
                    <div 
                      key={conv.conversation_id} 
                      className={`conversation-item ${selectedConversation?.id === conv.conversation_id ? 'selected' : ''}`}
                      onClick={() => handleConversationSelect(formattedConv)}
                    >
                      <div className="conversation-phone">
                        <Bot size={16} />
                        {formattedConv.phone}
                        {(formattedConv.source === 'eu' || conv.source === 'eu') ? (
                          <span className="conversation-source-badge source-eu" title="ElevenLabs EU">EU</span>
                        ) : (
                          <span className="conversation-source-badge source-global" title="ElevenLabs Global">Global</span>
                        )}
                      </div>
                      <div className="conversation-meta">
                        <span className="conversation-time">{formattedConv.time}</span>
                        <span className={`conversation-status ${formattedConv.status}`}></span>
                        <span className="conversation-duration">{formattedConv.duration}</span>
                      </div>
                      <div className="conversation-actions">
                        <button
                          className="view-transcription-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchConversationDetails(conv.conversation_id, formattedConv.source || conv.source || 'global');
                          }}
                          disabled={loadingDetails}
                        >
                          {loadingDetails ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <FileText size={14} />
                          )}
                          <span>Ver Transcripción</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-conversations-message">
                  <Bot size={48} className="no-conversations-icon" />
                  <h3>No hay conversaciones disponibles</h3>
                  <p>No se encontraron conversaciones para los agentes configurados.</p>
                </div>
              )
            )}
          </div>

          <div className="conversations-pagination">
            <div className="pagination-info">
              Página {currentPageNumber} 
              {hasMore ? ' (hay más)' : ' (última página)'}
              {elevenLabsConversations.length > 0 && ` - ${elevenLabsConversations.length} conversaciones en esta página`}
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange('prev')}
                disabled={cursorHistory.length === 0 || loadingConversations}
              >
                <ArrowLeft size={16} />
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange('next')}
                disabled={!hasMore || !nextCursor || loadingConversations}
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Panel derecho - Detalles de conversación */}
        {!selectedConversation ? (
          <div className="conversation-detail-panel">
            <div className="conversation-empty-state">
              <div className="empty-state-icon">
                <MessageSquare size={40} />
              </div>
              <div className="empty-state-title">Selecciona una conversación</div>
              <div className="empty-state-message">{t('conversations.selectConversation')}</div>
            </div>
          </div>
        ) : (
          <div className="conversation-detail-panel">
            {/* Panel de información de llamada */}
            <div className="call-info-sidebar">
              <div className="call-info-header">
                <div className="call-info-phone">
                  <Phone size={20} />
                  {getConversationDisplayData()?.phone || 'N/A'}
                </div>
                <a href="#" className="call-info-link">{t('conversations.contactInfo')}</a>
              </div>

              <div className="call-info-content">
                <div className="call-info-section">
                  <h4 className="call-info-section-title">{t('conversations.contactDetails')}</h4>
                  {getConversationDisplayData()?.nombre && (
                    <div className="call-info-row">
                      <span className="call-info-label">Nombre:</span>
                      <span className="call-info-value">{getConversationDisplayData()?.nombre || 'N/A'}</span>
                    </div>
                  )}
                  {getConversationDisplayData()?.titular && (
                    <div className="call-info-row">
                      <span className="call-info-label">Titular:</span>
                      <span className="call-info-value">{getConversationDisplayData()?.titular || 'N/A'}</span>
                    </div>
                  )}
                  {getConversationDisplayData()?.deParteDe && (
                    <div className="call-info-row">
                      <span className="call-info-label">De-parte-de:</span>
                      <span className="call-info-value">{getConversationDisplayData()?.deParteDe || 'N/A'}</span>
                    </div>
                  )}
                  {getConversationDisplayData()?.molesto !== null && getConversationDisplayData()?.molesto !== undefined && (
                    <div className="call-info-row">
                      <span className="call-info-label">Molesto:</span>
                      <span className="call-info-value">{String(getConversationDisplayData()?.molesto)}</span>
                    </div>
                  )}
                </div>

                <div className="call-info-section">
                  <h4 className="call-info-section-title">{t('conversations.callDetails')}</h4>
                  <div className="call-info-row">
                    <span className="call-info-label">{t('conversations.callOrigin')}</span>
                    <span className="call-info-value outbound">{getConversationDisplayData()?.callOrigin || 'N/A'} ↗</span>
                  </div>
                  <div className="call-info-row">
                    <span className="call-info-label">{t('conversations.agentVoice')}</span>
                    <span className="call-info-value">{getConversationDisplayData()?.agent || 'N/A'}</span>
                  </div>
                  <div className="call-info-row">
                    <span className="call-info-label">{t('conversations.campaign')}</span>
                    <span className="call-info-value campaign">{getConversationDisplayData()?.campaign || 'N/A'}</span>
                  </div>
                </div>

                <div className="call-summary-card">
                  <div className="call-summary-date">
                    {getConversationDisplayData()?.callDateTime ? 
                      getConversationDisplayData().callDateTime.toLocaleDateString('es-ES', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      }) : 
                      (getConversationDisplayData()?.callDate || 'Fecha no disponible')
                    }
                  </div>
                  <div className="call-summary-item">
                    <span className="call-summary-label">{t('conversations.callState')}</span>
                    <span className="call-summary-value completed">✓ {getConversationDisplayData()?.status || 'N/A'}</span>
                  </div>
                  <div className="call-summary-item">
                    <span className="call-summary-label">{t('conversations.result')}</span>
                    <span className="call-summary-value">{getConversationDisplayData()?.result || 'N/A'}</span>
                  </div>
                  <div className="call-summary-item">
                    <span className="call-summary-label">{t('conversations.duration')}</span>
                    <span className="call-summary-value">{getConversationDisplayData()?.duration || 'N/A'}</span>
                  </div>
                  <div className="call-summary-item">
                    <span className="call-summary-label">{t('conversations.hungUpBy')}</span>
                    <span className="call-summary-value">{getConversationDisplayData()?.hungUpBy || 'N/A'}</span>
                  </div>
                  <div className="call-summary-text">{getConversationDisplayData()?.summary || 'No hay resumen disponible'}</div>
                  
                  <button 
                    className="transcription-btn"
                    onClick={() => setShowTranscriptionModal(true)}
                    disabled={!conversationDetails}
                  >
                    <FileText size={16} />
                    <span>Ver Transcripción</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Transcripción */}
      {showTranscriptionModal && (
        <div className="transcription-modal-overlay">
          <div className="transcription-modal">
            <div className="modal-header">
              <div className="modal-title">
                <FileText size={20} />
                <span>Transcripción de Llamada</span>
              </div>
              <button 
                className="modal-close-btn"
                onClick={() => setShowTranscriptionModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-content">
              <div className="call-info-section">
                {conversationDetails ? (
                  <>
                    <div className="call-info-row">
                      <span className="call-info-label">ID Conversación:</span>
                      <span className="call-info-value">{conversationDetails.conversation_id || 'N/A'}</span>
                    </div>
                    <div className="call-info-row">
                      <span className="call-info-label">Agent ID:</span>
                      <span className="call-info-value">{conversationDetails.agent_id || 'N/A'}</span>
                    </div>
                    <div className="call-info-row">
                      <span className="call-info-label">Nombre del Agente:</span>
                      <span className="call-info-value">{conversationDetails.agent_name || 'N/A'}</span>
                    </div>
                    <div className="call-info-row">
                      <span className="call-info-label">Estado:</span>
                      <span className="call-info-value">{conversationDetails.status || 'N/A'}</span>
                    </div>
                    <div className="call-info-row">
                      <span className="call-info-label">Dirección:</span>
                      <span className="call-info-value">{conversationDetails.direction || 'N/A'}</span>
                    </div>
                    <div className="call-info-row">
                      <span className="call-info-label">Duración:</span>
                      <span className="call-info-value">
                        {conversationDetails.call_duration_secs ? 
                          `${Math.floor(conversationDetails.call_duration_secs / 60)}:${(conversationDetails.call_duration_secs % 60).toString().padStart(2, '0')}` : 
                          'N/A'
                        }
                      </span>
                    </div>
                    <div className="call-info-row">
                      <span className="call-info-label">Mensajes:</span>
                      <span className="call-info-value">{conversationDetails.message_count || 0}</span>
                    </div>
                    {conversationDetails.analysis?.data_collection_results && (
                      <>
                        {Object.keys(conversationDetails.analysis.data_collection_results).map((key) => {
                          const field = conversationDetails.analysis.data_collection_results[key];
                          
                          // Extraer el valor correctamente - field puede ser un objeto con {value, data_collection_id, etc.}
                          let displayValue = 'N/A';
                          if (field) {
                            if (typeof field === 'object' && field !== null) {
                              // Si es un objeto, buscar la propiedad 'value'
                              if ('value' in field) {
                                const val = field.value;
                                // Convertir a string si es necesario
                                if (val === null || val === undefined) {
                                  displayValue = 'N/A';
                                } else if (typeof val === 'object') {
                                  // Si value es un objeto, convertirlo a JSON string
                                  displayValue = JSON.stringify(val);
                                } else if (Array.isArray(val)) {
                                  // Si es un array, unirlo con comas
                                  displayValue = val.join(', ');
                                } else {
                                  displayValue = String(val);
                                }
                              } else {
                                // Si no tiene 'value', intentar convertir el objeto a string
                                displayValue = JSON.stringify(field);
                              }
                            } else {
                              // Si no es un objeto, usar directamente
                              displayValue = String(field);
                            }
                          }
                          
                          return (
                            <div key={key} className="call-info-row">
                              <span className="call-info-label">{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
                              <span className="call-info-value">{displayValue}</span>
                            </div>
                          );
                        })}
                      </>
                    )}
                    {conversationDetails.analysis?.outcome && (
                      <div className="call-info-row">
                        <span className="call-info-label">Resultado:</span>
                        <span className="call-info-value">{conversationDetails.analysis.outcome}</span>
                      </div>
                    )}
                    {conversationDetails.analysis?.hung_up_by && (
                      <div className="call-info-row">
                        <span className="call-info-label">Colgado por:</span>
                        <span className="call-info-value">{conversationDetails.analysis.hung_up_by}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-data-message">
                    <p>No hay datos de conversación disponibles.</p>
                  </div>
                )}
              </div>

              <div className="transcription-content">
                <div className="transcription-header">
                  <MessageSquare size={16} />
                  <span>Conversación Completa</span>
                </div>
                
                <div className="transcription-messages">
                  {conversationDetails?.transcript ? (
                    conversationDetails.transcript.map((message, index) => (
                      <div key={index} className={`transcription-message ${message.role === 'user' ? 'customer' : 'agent'}`}>
                        <div className="message-bubble">
                          <div className="message-header">
                            {message.role === 'user' ? 'CLIENTE' : 'AGENTE VIRTUAL'}
                          </div>
                          <div className="message-text">{message.message}</div>
                          <span className="message-time">
                            {message.time_in_call_secs ? `${message.time_in_call_secs}s` : 'N/A'}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-transcription-message">
                      <p>No hay transcripción disponible.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {conversationDetails ? (
                <>
                  <button 
                    className="download-btn call-download"
                    onClick={() => downloadAudio(conversationDetails.conversation_id, selectedConversation?.source || selectedConversation?.originalData?.source || 'global')}
                  >
                    <Download size={16} />
                    <span>Descargar Audio</span>
                  </button>
                  <button 
                    className="download-btn pdf-download"
                    onClick={() => downloadPDF(conversationDetails.conversation_id, selectedConversation?.source || selectedConversation?.originalData?.source || 'global')}
                  >
                    <FileText size={16} />
                    <span>Descargar PDF</span>
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationsPanel;